/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { countries } from 'components/Common/Countries';
import { ModuleContentTooltip } from 'components/Common/MUIStyledComponents';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import LockIcon from '@mui/icons-material/Lock';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import CloseIcon from '@mui/icons-material/Close';

interface InputFormProps {
  onSubmit: (inputValue: string, country: string) => void;
  isStreaming?: boolean;
  initialValue?: string;
  /** a label from components/Common/Countries */
  initialCountry?: string;
  referenceHts?: string;
  userHasSearch?: boolean;
  hasLicense?: boolean;
  hasFinished?: boolean;
  licenseMsg?: any;
  setModalOpen?: any;
  isInteractionDisabled?: boolean;
}

const ECCNForm: React.FC<InputFormProps> = ({
  onSubmit,
  isStreaming,
  initialValue,
  initialCountry,
  referenceHts,
  hasLicense,
  licenseMsg,
  hasFinished,
  setModalOpen,
  isInteractionDisabled = false,
}) => {
  const [inputValue, setInputValue] = useState<string>(initialValue || '');
  const [selectedCountry, setSelectedCountry] = useState(initialCountry || '');

  const handleEnterPress = (e: any) => {
    if (isInteractionDisabled) {
      return;
    }
    if (e.key === 'Enter' || e.which === 13) {
      onSubmit(inputValue, selectedCountry);
    }
  };

  useEffect(() => {
    if (initialValue !== undefined) {
      setInputValue(initialValue);
    }
  }, [initialValue]);

  return (
    <>
      <div className='screening-search-container-full' style={{ gap: '14px' }}>
        <div className='screening-inputs-list'>
          <div className='screening-input-row'>
            <div className='screening-label-input'>
              <div style={{ width: '-webkit-fill-available' }}>
                <div className='screening-input-group' style={{ marginTop: 0 }}>
                  <div className='screening-input-minors'>
                    {/* HTS Code */}
                    <label
                      className='entity-screening-field'
                      style={{ width: '50%' }}
                    >
                      <span>ECCN</span>
                      <div className='classify-cont' style={{ padding: 0 }}>
                        <div className='classify-cont-sec'>
                          <div className='classify-cont-sec-div'>
                            <input
                              placeholder='Enter your ECCN'
                              className='classify-cont-sec-input'
                              type='text'
                              value={inputValue}
                              onChange={(e) => setInputValue(e.target.value)}
                              style={{ height: '44px', minHeight: '44px' }}
                            // onKeyUp={handleEnterPress}
                            />
                          </div>
                        </div>
                      </div>
                      {referenceHts && (
                        <div className='eccn-reference-note'>
                          HTS {referenceHts} from your screen
                        </div>
                      )}
                    </label>

                    {/* Origin Country */}
                    <label
                      className='entity-screening-field'
                      style={{ width: '50%' }}
                    >
                      <span>Country</span>
                      <Autocomplete
                        options={countries}
                        getOptionLabel={(option) => option.label}
                        componentsProps={{
                          popper: {
                            sx: {
                              '& .MuiAutocomplete-option': {
                                color: 'var(--text-primary-white) !important',
                              },
                            }, // Higher z-index to sit above sidebar/modals
                          },
                          paper: {
                            sx: {
                              color: 'var(--text-primary-white) !important',
                            },
                          },
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            height: '44px',
                            borderRadius: '8px',
                            background: 'var(--Background-200, var(--bg-colors))',
                            border: '1px solid var(--es-soft-border)',
                            color: 'var(--text-primary-color)',
                            '& .MuiOutlinedInput-notchedOutline': {
                              border: 'none',
                            },
                          },
                          '& .MuiAutocomplete-inputRoot': {
                            paddingTop: '0 !important',
                            paddingBottom: '0 !important',
                          },
                          '& .MuiInputBase-input': {
                            color: 'var(--text-primary-color)',
                            fontFamily: 'Inter',
                            fontSize: '14px',
                          },
                          '& .MuiSvgIcon-root': {
                            color: 'var(--text-secondary)',
                          },
                          '& .MuiAutocomplete-clearIndicator': {
                            display: 'none',
                          },
                        }}
                        onChange={(event, newValue) => {
                          setSelectedCountry(newValue ? newValue.label : ''); // Store only the label
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder='Select country'
                            onKeyUp={handleEnterPress}
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <>
                                  <PublicOutlinedIcon
                                    sx={{
                                      color: 'var(--text-secondary)',
                                      fontSize: 20,
                                      marginRight: '8px',
                                    }}
                                  />
                                  {params.InputProps.startAdornment}
                                </>
                              ),
                              endAdornment: (
                                <>
                                  {selectedCountry && (
                                    <span
                                      className='entity-screening-input-clear'
                                      role='button'
                                      aria-label='Clear country'
                                      onMouseDown={(e) => e.preventDefault()}
                                      onClick={() => setSelectedCountry('')}
                                    >
                                      <CloseIcon sx={{ fontSize: 16 }} />
                                    </span>
                                  )}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                            InputLabelProps={{
                              style: {
                                color: 'var(--text-primary)',
                              },
                            }}
                          />
                        )}
                        value={
                          countries.find(
                            (country) => country.label === selectedCountry,
                          ) || null
                        }
                        disabled={isInteractionDisabled}
                      />
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='screening-filters-cont'>
          <div className='screening-filters-group'>
            <div className='disclaimer-text'>
              Disclaimer: This tool provides automated determinations of potential export controls based on ECCN and country inputs. While efforts are made to reflect applicable regulations, no guarantee is made as to the accuracy, completeness, or current applicability of the results.
              <br /><br />
              Export control requirements may vary based on additional factors not captured by this tool, including end use, end user, and specific transaction details. Users are solely responsible for validating all outputs and ensuring compliance with applicable export control laws and regulations, including the U.S. Export Administration Regulations (EAR).
            </div>
            <div
              style={{
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
                marginLeft: 'auto',
              }}
            >
              <ModuleContentTooltip
                title={
                  <p className='module-header-tooltip-text'>
                    Enter an ECCN and Country and let BITE&apos;s AI generate
                    the relevant licensing and control data for your product.
                  </p>
                }
                placement='top'
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      display: 'flex',
                      padding: 'var(--spacing-md, 8px) var(--spacing-lg, 12px)',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      alignSelf: 'stretch',
                      borderRadius: 'var(--radius-md, 8px)',
                      background: 'var(--Background-200, var(--bg-colors))',
                      color: 'var(--text-prim25)',
                      maxWidth: '400px',
                      '& .MuiTooltip-arrow': {
                        color: 'var(--Background-200, var(--bg-colors))',
                      },
                    },
                  },
                }}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='18'
                  height='18'
                  viewBox='0 0 18 18'
                  fill='none'
                >
                  <path
                    d='M9 8.25C8.80109 8.25 8.61033 8.32902 8.46967 8.46967C8.32902 8.61032 8.25 8.80109 8.25 9V12C8.25 12.1989 8.32902 12.3897 8.46967 12.5303C8.61033 12.671 8.80109 12.75 9 12.75C9.19892 12.75 9.38968 12.671 9.53033 12.5303C9.67099 12.3897 9.75 12.1989 9.75 12V9C9.75 8.80109 9.67099 8.61032 9.53033 8.46967C9.38968 8.32902 9.19892 8.25 9 8.25ZM9.285 5.31C9.10241 5.23499 8.8976 5.23499 8.715 5.31C8.62294 5.3457 8.53883 5.39922 8.4675 5.4675C8.40126 5.5404 8.34799 5.62411 8.31 5.715C8.26802 5.80401 8.24747 5.90162 8.25 6C8.24943 6.0987 8.26835 6.19655 8.30568 6.28793C8.343 6.37931 8.39799 6.46242 8.4675 6.5325C8.5404 6.59875 8.62412 6.65202 8.715 6.69C8.82863 6.73668 8.95198 6.75474 9.07421 6.74258C9.19645 6.73043 9.31383 6.68844 9.41604 6.6203C9.51825 6.55216 9.60215 6.45996 9.66039 6.3518C9.71862 6.24364 9.74939 6.12284 9.75 6C9.74724 5.80142 9.66955 5.61123 9.5325 5.4675C9.46118 5.39922 9.37707 5.3457 9.285 5.31ZM9 1.5C7.51664 1.5 6.0666 1.93987 4.83323 2.76398C3.59986 3.58809 2.63856 4.75943 2.07091 6.12987C1.50325 7.50032 1.35472 9.00832 1.64411 10.4632C1.9335 11.918 2.64781 13.2544 3.6967 14.3033C4.7456 15.3522 6.08197 16.0665 7.53683 16.3559C8.99168 16.6453 10.4997 16.4968 11.8701 15.9291C13.2406 15.3614 14.4119 14.4001 15.236 13.1668C16.0601 11.9334 16.5 10.4834 16.5 9C16.5 8.01509 16.306 7.03982 15.9291 6.12987C15.5522 5.21993 14.9997 4.39314 14.3033 3.6967C13.6069 3.00026 12.7801 2.44781 11.8701 2.0709C10.9602 1.69399 9.98492 1.5 9 1.5ZM9 15C7.81332 15 6.65328 14.6481 5.66658 13.9888C4.67989 13.3295 3.91085 12.3925 3.45673 11.2961C3.0026 10.1997 2.88378 8.99334 3.11529 7.82946C3.3468 6.66557 3.91825 5.59647 4.75736 4.75736C5.59648 3.91824 6.66558 3.3468 7.82946 3.11529C8.99335 2.88378 10.1997 3.0026 11.2961 3.45672C12.3925 3.91085 13.3295 4.67988 13.9888 5.66658C14.6481 6.65327 15 7.81331 15 9C15 10.5913 14.3679 12.1174 13.2426 13.2426C12.1174 14.3679 10.5913 15 9 15Z'
                    fill='var(--alternative-text-color)'
                  />
                </svg>
              </ModuleContentTooltip>
              {hasFinished ? (
                <div className='screening-input-submit-group'>
                  <button
                    className='btn-new-automate'
                    onClick={() => {
                      if (!isInteractionDisabled) {
                        setModalOpen(true);
                      }
                    }}
                    disabled={isInteractionDisabled}
                  >
                    <div>
                      {isInteractionDisabled && <LockIcon fontSize='small' />}
                      Save
                    </div>
                  </button>
                </div>
              ) : (
                <div className='screening-input-submit-group'>
                  <button className='btn-new-automate-disabled'>
                    <div>Save</div>
                  </button>
                </div>
              )}
              <div className='screening-input-submit-group'>
                <button
                  className={
                    isInteractionDisabled
                      ? 'btn-new-automate-disabled'
                      : 'btn-new-automate'
                  }
                  style={{ minWidth: '140px' }}
                  onClick={() => {
                    if (!isInteractionDisabled) {
                      onSubmit(inputValue, selectedCountry);
                    }
                  }}
                  disabled={isStreaming || isInteractionDisabled}
                >
                  <div>
                    {isInteractionDisabled && <LockIcon fontSize='small' />}
                    Check Controls
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>

        {hasLicense &&
          licenseMsg?.result?.heading ===
          'License Requirement Important Note!' && (
            <div className='screening-ai-containers-warning'>
              <div style={{ width: '100%' }}>
                <div className='api-container-left'>
                  <div
                    className='screening-ai-label-text'
                    style={{
                      marginBottom: '0.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#FACC14',
                    }}
                  >
                    <WarningAmberOutlinedIcon
                      style={{ marginRight: '10px', color: '#FACC14' }}
                    />
                    License Requirement Important Note!
                  </div>
                  <div className='screening-ai-sum-groups'>
                    <div
                      className='screening-ai-sum-header-texts'
                      style={{ color: 'var(--bg-license)' }}
                    >
                      {licenseMsg?.result?.text}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
      </div>
    </>
  );
};

export default ECCNForm;
